import { create } from 'zustand'
import { notificationService } from '../services/api/notificationService'
import { connectSocket, disconnectSocket } from '../services/socket'

const DEFAULT_LIST_META = { page: 1, limit: 10, totalPages: 1, totalResults: 0, status: 'all' }

const updateEntry = (arr, id, patch) => arr.map((n) => (n.id === id ? { ...n, ...patch } : n))
const removeEntry = (arr, id) => arr.filter((n) => n.id !== id)

const useNotificationStore = create((set, get) => ({
  // Small "recent" list backing the header dropdown
  preview: [],
  isPreviewLoading: false,

  // Full paginated/filterable list backing the Notifications page
  list: [],
  listMeta: DEFAULT_LIST_META,
  isListLoading: false,

  // Always-fresh badge count, kept in sync via REST + socket
  unreadCount: 0,

  isSocketConnected: false,

  // Called once on app load / login: connect the socket and seed initial state
  init: async (token) => {
    get().connect(token)
    await Promise.all([
      get().fetchUnreadCount(),
      get().fetchPreview(),
    ])
  },

  connect: (token) => {
    if (!token) return
    const socket = connectSocket(token)
    if (!socket) return
    socket.off('notification', get().handleSocketNotification)
    socket.on('notification', get().handleSocketNotification)
    set({ isSocketConnected: true })
  },

  disconnect: () => {
    disconnectSocket()
    set({ isSocketConnected: false })
  },

  handleSocketNotification: (payload) => {
    if (!payload?.notification) return
    const { notification, unreadCount } = payload

    set((state) => {
      const alreadyInPreview = state.preview.some((n) => n.id === notification.id)
      const preview = alreadyInPreview
        ? state.preview
        : [notification, ...state.preview].slice(0, 10)

      // Only splice into the full list live when it's showing the first page
      // of "all"/"unread" — a filtered "read" view or a later page shouldn't
      // suddenly grow at the top.
      const shouldSpliceIntoList =
        state.listMeta.page === 1 && state.listMeta.status !== 'read'
      const alreadyInList = state.list.some((n) => n.id === notification.id)
      const list =
        shouldSpliceIntoList && !alreadyInList
          ? [notification, ...state.list]
          : state.list
      const listMeta =
        shouldSpliceIntoList && !alreadyInList
          ? { ...state.listMeta, totalResults: state.listMeta.totalResults + 1 }
          : state.listMeta

      return {
        preview,
        list,
        listMeta,
        unreadCount: typeof unreadCount === 'number' ? unreadCount : state.unreadCount,
      }
    })
  },

  fetchUnreadCount: async () => {
    try {
      const response = await notificationService.getUnreadCount()
      set({ unreadCount: response.data?.unreadCount ?? 0 })
    } catch (error) {
      console.error('Error fetching unread notification count:', error)
    }
  },

  // Seeds the header dropdown with the most recent unread notifications
  fetchPreview: async () => {
    set({ isPreviewLoading: true })
    try {
      const response = await notificationService.getNotifications({ status: 'unread', limit: 10 })
      set({
        preview: response.data || [],
        unreadCount: response.unreadCount ?? get().unreadCount,
      })
    } catch (error) {
      console.error('Error fetching notification preview:', error)
    } finally {
      set({ isPreviewLoading: false })
    }
  },

  // Drives the full Notifications page (status filter + pagination)
  fetchList: async (params = {}) => {
    set({ isListLoading: true })
    try {
      const response = await notificationService.getNotifications(params)
      set({
        list: response.data || [],
        unreadCount: response.unreadCount ?? get().unreadCount,
        listMeta: {
          page: response.page || params.page || 1,
          limit: response.limit || params.limit || 10,
          totalPages: response.totalPages || 1,
          totalResults: response.totalResults ?? (response.data || []).length,
          status: params.status || 'all',
        },
      })
      return response
    } catch (error) {
      console.error('Error fetching notifications:', error)
      throw error
    } finally {
      set({ isListLoading: false })
    }
  },

  markAsRead: async (id) => {
    const wasUnread = get().preview.some((n) => n.id === id && !n.read) ||
      get().list.some((n) => n.id === id && !n.read)

    set((state) => ({
      preview: updateEntry(state.preview, id, { read: true }),
      list: updateEntry(state.list, id, { read: true }),
      unreadCount: wasUnread ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
    }))

    try {
      await notificationService.markAsRead(id)
    } catch (error) {
      console.error('Error marking notification as read:', error)
      throw error
    }
  },

  markAsUnread: async (id) => {
    const wasRead = get().preview.some((n) => n.id === id && n.read) ||
      get().list.some((n) => n.id === id && n.read)

    set((state) => ({
      preview: updateEntry(state.preview, id, { read: false }),
      list: updateEntry(state.list, id, { read: false }),
      unreadCount: wasRead ? state.unreadCount + 1 : state.unreadCount,
    }))

    try {
      await notificationService.markAsUnread(id)
    } catch (error) {
      console.error('Error marking notification as unread:', error)
      throw error
    }
  },

  markAllAsRead: async () => {
    set((state) => ({
      preview: state.preview.map((n) => ({ ...n, read: true })),
      list: state.list.map((n) => ({ ...n, read: true })),
      unreadCount: 0,
    }))

    try {
      await notificationService.markAllAsRead()
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      throw error
    }
  },

  deleteNotification: async (id) => {
    const target = get().preview.find((n) => n.id === id) || get().list.find((n) => n.id === id)

    set((state) => ({
      preview: removeEntry(state.preview, id),
      list: removeEntry(state.list, id),
      unreadCount: target && !target.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
      listMeta: { ...state.listMeta, totalResults: Math.max(0, state.listMeta.totalResults - 1) },
    }))

    try {
      await notificationService.deleteNotification(id)
    } catch (error) {
      console.error('Error deleting notification:', error)
      throw error
    }
  },

  deleteAllRead: async () => {
    set((state) => {
      const removedCount = state.list.filter((n) => n.read).length
      return {
        preview: state.preview.filter((n) => !n.read),
        list: state.list.filter((n) => !n.read),
        listMeta: { ...state.listMeta, totalResults: Math.max(0, state.listMeta.totalResults - removedCount) },
      }
    })

    try {
      await notificationService.deleteAllRead()
    } catch (error) {
      console.error('Error deleting read notifications:', error)
      throw error
    }
  },

  // Called on logout: tear down the socket and clear all in-memory state
  reset: () => {
    get().disconnect()
    set({
      preview: [],
      isPreviewLoading: false,
      list: [],
      listMeta: DEFAULT_LIST_META,
      isListLoading: false,
      unreadCount: 0,
    })
  },
}))

export default useNotificationStore
